import { type MouseEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r, s } from '@/theme/tokens';
import {
  Autocomplete,
  Box,
  CloseIcon,
  KeyboardArrowDownIcon,
  Text,
  TextField,
} from '@/ui/primitives';
import { isFilterIndicatorTarget } from './filterIndicatorTargets';
import { filterTokens } from './filterTokens';
import {
  buildTagsFilterAutocompleteSx,
  buildTagsFilterDropdownPaperSx,
  buildTagsFilterListboxSx,
  buildTagsFilterPopperModifiers,
  buildTagsFilterPopperSx,
  tagsFilterClearIndicatorSx,
} from './tagsFilterControlStyles';
import {
  resolveTagsFilterDropdownWidthPx,
  tagsFilterDropdownViewportMaxWidth,
} from './tagsFilterDropdownSizing';
import { TagsFilterFieldOption } from './TagsFilterFieldOption';
import { TagsFilterFieldSelectedValue } from './TagsFilterFieldSelectedValue';
import { useTagsFilterDropdownOpen } from './useTagsFilterDropdownOpen';
import type { TagOption, TagsFilterFieldProps } from './TagsFilterFieldTypes';

const filterPopperDataAttribute = { 'data-flowqa-filter-popper': 'true' } as const;
const defaultFilterWidthPx = 216;

const isAutocompleteDropdownTarget = (target: EventTarget): boolean =>
  target instanceof Element &&
  target.closest(
    '.MuiAutocomplete-option, .MuiAutocomplete-listbox, .MuiAutocomplete-paper, .MuiAutocomplete-popper',
  ) !== null;

const isSelectedTagRemoveTarget = (target: EventTarget): boolean =>
  target instanceof Element && target.closest('[data-flowqa-tag-chip-remove="true"]') !== null;

export const TagsFilterField = ({
  value,
  defaultValue = [],
  options,
  label = getFilterLabel('tags'),
  showLabel = true,
  fontSizePx = 13,
  optionFontSizePx = fontSizePx,
  labelPlacement: _labelPlacement,
  labelControlGap: _labelControlGap,
  labelRole: _labelRole = 'body',
  labelColor: _labelColor = 'textPrimary',
  emptyLabel = getFilterEmptyLabel('allTags'),
  emptyOptionsAction,
  inputAriaLabel,
  fullWidth = false,
  controlWidthPx,
  dropdownWidthPx,
  inputBackgroundColor = 'var(--control-surface)',
  dropdownBackgroundColor = 'var(--floating-control-surface)',
  inputBorderColor,
  inputLeftPadding = s('sm'),
  selectedValueLeftPadding = inputLeftPadding,
  controlHeight = 34,
  controlRadius = r('sm'),
  highlightActiveValue = true,
  showClearButton = true,
  multiple = true,
  optionPresentation = 'text',
  showSelectedChipRemoveButtons = false,
  renderOptionLabel,
  open,
  onOpenChange,
  onOptionSelect,
  onChange,
}: TagsFilterFieldProps): ReactElement => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const emptyOptionValue = '__flowqa_no_options__';
  const actionOptionValue = '__flowqa_tag_action__';
  const actionOption = { value: actionOptionValue, label: 'Add New' };
  const effectiveOptions = [
    ...(options.length > 0 ? options : [{ value: emptyOptionValue, label: 'No options' }]),
    ...(emptyOptionsAction ? [actionOption] : []),
  ];
  const selectedOptions = options.filter((option) => value.includes(option.value));
  const hasValue = selectedOptions.length > 0;
  const hasActiveFilterValue =
    highlightActiveValue &&
    (value.length !== defaultValue.length ||
      value.some((optionValue) => !defaultValue.includes(optionValue)));
  const shouldUseCompactSelectedInput = hasValue;
  const inputLeftInset = shouldUseCompactSelectedInput ? s('none') : inputLeftPadding;
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;
  const resolvedControlWidth = controlWidthPx ? `${controlWidthPx}px` : fixedFilterWidth;
  const optionRowHeight = `${filterTokens.optionRowMinHeight}px`;
  const optionHorizontalPadding = s('md');
  const optionRightPadding = s('md');
  const optionCheckboxSlotWidth = `calc(${s('lg')} + ${s('md')})`;
  const [measuredControlWidthPx, setMeasuredControlWidthPx] = useState(
    controlWidthPx ?? defaultFilterWidthPx,
  );
  const controlWidthValuePx = controlWidthPx ?? measuredControlWidthPx;
  const selectedInputWidth = hasValue ? '0px' : 'auto';
  const selectedInputFlex = hasValue ? '0 0 0px' : '1 1 auto';
  const endAdornmentPadding =
    selectedOptions.length > 0
      ? `calc(${s('2xl')} + ${s('md')})`
      : `calc(${s('2xl')} + ${s('lg')})`;
  const shouldShowLabel = showLabel && label.length > 0;
  const showOptionChips = optionPresentation === 'tagChip';
  const hasCountTextColumn = options.some((option) => option.count !== undefined);
  const hasCountColumn = options.some(
    (option) => option.count !== undefined || option.countIcon !== undefined,
  );
  const hasCountIconColumn = options.some((option) => option.countIcon !== undefined);
  const optionGridTemplateColumns = hasCountColumn
    ? `${multiple ? `${optionCheckboxSlotWidth} ` : ''}minmax(0, 1fr) ${
        hasCountTextColumn ? 'max-content' : '0'
      }`
    : undefined;
  const countedDropdownWidthPx = resolveTagsFilterDropdownWidthPx({
    controlWidthPx: controlWidthValuePx,
    hasCountColumn,
    hasCountIconColumn,
    showOptionChips,
    viewportWidthPx: typeof window === 'undefined' ? undefined : window.innerWidth,
  });
  const measuredControlWidth = `${controlWidthValuePx}px`;
  const resolvedCustomDropdownWidthPx = dropdownWidthPx;
  const resolvedDropdownMinWidth =
    resolvedCustomDropdownWidthPx !== undefined
      ? `${String(resolvedCustomDropdownWidthPx)}px`
      : hasCountTextColumn
        ? `${String(countedDropdownWidthPx)}px`
        : measuredControlWidth;
  const resolvedDropdownWidth = resolvedDropdownMinWidth;
  const resolvedListboxWidth = hasCountColumn ? '100%' : resolvedDropdownWidth;
  const { isDropdownOpen, isDropdownClosing, setDropdownOpen, shouldRenderDropdown } =
    useTagsFilterDropdownOpen({ open, onOpenChange });
  const toggleDropdownFromControl = (event: MouseEvent<HTMLDivElement>): void => {
    if (
      isFilterIndicatorTarget(event.target) ||
      isAutocompleteDropdownTarget(event.target) ||
      isSelectedTagRemoveTarget(event.target)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (isDropdownOpen) {
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(true);
  };
  useEffect(() => {
    const element = controlRef.current;
    if (!element) {
      return;
    }

    const updateControlWidth = (): void => {
      const nextWidth = Math.round(element.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setMeasuredControlWidthPx(nextWidth);
      }
    };

    updateControlWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateControlWidth);
      return () => {
        window.removeEventListener('resize', updateControlWidth);
      };
    }

    const observer = new ResizeObserver(() => {
      updateControlWidth();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [controlWidthPx, fullWidth]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const closeOnOutsidePointerDown = (event: globalThis.MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (controlRef.current?.contains(target)) {
        return;
      }
      if (
        target instanceof Element &&
        target.closest(
          '[data-flowqa-filter-popper="true"], .MuiAutocomplete-popper, .MuiAutocomplete-paper, .MuiAutocomplete-listbox',
        )
      ) {
        return;
      }
      setDropdownOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePointerDown);
    document.addEventListener('touchstart', closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointerDown);
      document.removeEventListener('touchstart', closeOnOutsidePointerDown);
    };
  }, [isDropdownOpen, setDropdownOpen]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: shouldShowLabel ? s('sm') : s('none'),
        minWidth: 0,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {shouldShowLabel ? (
        <Text
          role="body"
          color="textPrimary"
          transform="none"
          sx={{
            whiteSpace: 'normal',
            fontSize: `${fontSizePx}px`,
            lineHeight: 1.4,
            fontWeight: (theme) => theme.typography.fontWeightRegular,
          }}
        >
          {label}
        </Text>
      ) : null}
      <Box
        ref={controlRef}
        onMouseDownCapture={toggleDropdownFromControl}
        sx={{
          width: fullWidth ? '100%' : resolvedControlWidth,
          minWidth: fullWidth ? 0 : resolvedControlWidth,
          maxWidth: fullWidth ? 'none' : resolvedControlWidth,
          flex: undefined,
        }}
      >
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          disableClearable={!showClearButton}
          disablePortal={false}
          fullWidth={false}
          open={shouldRenderDropdown}
          onOpen={() => {
            setDropdownOpen(true);
          }}
          onClose={(_event, reason) => {
            if (reason === 'blur') {
              return;
            }
            if (multiple && (reason === 'selectOption' || reason === 'removeOption')) {
              return;
            }
            setDropdownOpen(false);
          }}
          clearIcon={<CloseIcon fontSize="small" />}
          popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
          options={effectiveOptions}
          value={multiple ? selectedOptions : (selectedOptions[0] ?? null)}
          isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => option.value === emptyOptionValue}
          onChange={(_event, nextValue) => {
            setDropdownOpen(multiple);
            const nextOptions: TagOption[] = Array.isArray(nextValue)
              ? (nextValue as TagOption[])
              : nextValue === null
                ? []
                : [nextValue as TagOption];
            if (nextOptions.length > 0) {
              const selectedOption = multiple
                ? nextOptions[nextOptions.length - 1]
                : nextOptions[0];
              if (
                selectedOption.value !== emptyOptionValue &&
                selectedOption.value !== actionOptionValue &&
                onOptionSelect?.(selectedOption.value) === false
              ) {
                setDropdownOpen(false);
                return;
              }
            }
            onChange(
              nextOptions
                .map((option) => option.value)
                .filter(
                  (optionValue) =>
                    optionValue !== emptyOptionValue && optionValue !== actionOptionValue,
                ),
            );
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <TagsFilterFieldOption
                actionOptionValue={actionOptionValue}
                emptyOptionsAction={emptyOptionsAction}
                emptyOptionValue={emptyOptionValue}
                hasCountColumn={hasCountColumn}
                multiple={multiple}
                option={option}
                optionCheckboxSlotWidth={optionCheckboxSlotWidth}
                optionGridTemplateColumns={optionGridTemplateColumns}
                optionFontSizePx={optionFontSizePx}
                optionKey={key}
                optionProps={optionProps}
                optionRowHeight={optionRowHeight}
                renderOptionLabel={renderOptionLabel}
                selected={selected}
                showOptionChips={showOptionChips}
                onActionClick={() => {
                  setDropdownOpen(false);
                }}
                onOptionSelect={(optionValue) => {
                  const shouldContinue = onOptionSelect?.(optionValue);
                  if (shouldContinue === false) {
                    setDropdownOpen(false);
                  }
                  return shouldContinue;
                }}
              />
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              fieldStyle="filter"
              inputHeight={controlHeight}
              inputBackgroundColor={inputBackgroundColor}
              placeholder={!hasValue ? emptyLabel : undefined}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label,
                readOnly: true,
                style: { cursor: 'pointer' },
              }}
            />
          )}
          sx={buildTagsFilterAutocompleteSx({
            controlHeight,
            controlRadius,
            endAdornmentPadding,
            fontSizePx,
            hasActiveFilterValue,
            inputBackgroundColor,
            inputBorderColor,
            inputLeftInset,
            selectedInputFlex,
            selectedInputWidth,
            shouldUseCompactSelectedInput,
          })}
          slotProps={{
            popper: {
              ...filterPopperDataAttribute,
              placement: 'bottom-start',
              popperOptions: {
                modifiers: buildTagsFilterPopperModifiers(),
              },
              modifiers: buildTagsFilterPopperModifiers(),
              sx: buildTagsFilterPopperSx({
                dropdownMaxWidth: tagsFilterDropdownViewportMaxWidth,
                hasCountColumn,
                minWidth: resolvedDropdownMinWidth,
                optionGridTemplateColumns,
                resolvedListboxWidth,
                width: resolvedDropdownWidth,
              }),
            },
            paper: {
              sx: buildTagsFilterDropdownPaperSx({
                backgroundColor: dropdownBackgroundColor,
                controlRadius,
                dropdownMaxWidth: tagsFilterDropdownViewportMaxWidth,
                isDropdownClosing,
                minWidth: resolvedDropdownMinWidth,
                width: resolvedDropdownWidth,
              }),
            },
            clearIndicator: {
              sx: tagsFilterClearIndicatorSx,
            },
            listbox: {
              sx: buildTagsFilterListboxSx({
                dropdownMaxWidth: tagsFilterDropdownViewportMaxWidth,
                hasCountColumn,
                minWidth: resolvedDropdownMinWidth,
                optionGridTemplateColumns,
                optionHorizontalPadding,
                optionRightPadding,
                optionRowHeight,
                resolvedListboxWidth,
              }),
            },
          }}
          renderValue={(selected, getSelectedItemProps) => {
            const selectedValues = Array.isArray(selected) ? selected : [selected];
            return (
              <TagsFilterFieldSelectedValue
                controlHeight={controlHeight}
                getSelectedItemProps={getSelectedItemProps}
                inputLeftPadding={selectedValueLeftPadding}
                onRemoveValue={
                  showSelectedChipRemoveButtons
                    ? (optionValue) => {
                        onChange(value.filter((selectedValue) => selectedValue !== optionValue));
                      }
                    : undefined
                }
                selectedValues={selectedValues}
                showOptionChips={showOptionChips}
              />
            );
          }}
        />
      </Box>
    </Box>
  );
};

export type { TagOption, TagsFilterFieldProps };
